import { CoreCourseToSemesterString } from "./../database/core-course-data";
import { CoreCourseComparator } from "../database/core-course-data";
import { GetCourseFirstRelations } from "../dataparsers/coursemapParser";
import { HeatmapArea } from "../datastructures/coursedata";
import {
  EdgeMode,
  IConnector,
  ICourseNode,
  Vector2,
} from "../datastructures/dataviz";

const RadialLayoutCourseNodes = (
  NodeList: ICourseNode[],
  CenterPoint: Vector2,
  area: number,
  focusedNode: ICourseNode
): ICourseNode[] => {
  var RadialGraph: ICourseNode[] = NodeList;
  var alphaSortedChildren: ICourseNode[];

  //core course exception -- custom sorting
  if (area !== 0) {
    alphaSortedChildren = RadialGraph.filter((el) => el.homeArea === area)
      .filter((el) => el !== focusedNode)
      .sort((a, b) => a.data.courseCode.localeCompare(b.data.courseCode));
  } else {
    // For core area, exclude courses that have an empty courseAreas array
    alphaSortedChildren = RadialGraph.filter(
      (el) =>
        el.homeArea === area &&
        Array.isArray(el.data.courseAreas) &&
        el.data.courseAreas.length > 0
    )
      .filter((el) => el !== focusedNode)
      .sort((a, b) =>
        CoreCourseComparator(a.data.courseCode, b.data.courseCode)
      )
      .reverse();
  }

  var outerNodesCount = alphaSortedChildren.length; //-1 for center
  var outerRadius = 320; //should be a fn of number of nodes ...

  var firstRelations;
  var innerNodesCount;
  var outerRelations: ICourseNode[];

  if (focusedNode != null) {
    firstRelations = GetCourseFirstRelations(
      NodeList,
      focusedNode.data.courseCode
    ).filter(
      (n) =>
        n.homeArea === area &&
        (area !== 0 ||
          (Array.isArray(n.data.courseAreas) && n.data.courseAreas.length > 0))
    );
    innerNodesCount = firstRelations.length;
    outerRelations = alphaSortedChildren.filter(
      (n) => !firstRelations.includes(n)
    );
  } else {
    firstRelations = [];
    innerNodesCount = 0;
    outerRelations = alphaSortedChildren;
  }

  outerNodesCount -= innerNodesCount;

  RadialGraph.forEach((node) => {
    if (node.homeArea !== area) return; //equiv. to continue

    var orderInCluster = firstRelations.includes(node)
      ? firstRelations.indexOf(node)
      : outerRelations.indexOf(node);
    // var phaseShift = innerNodesCount%2 === outerNodesCount%2 ? 0.5 : 0;
    // inner courses aligned on 0 axis
    var outerPhaseShift = 0;
    var innerAxial = innerNodesCount % 2 === 0 || innerNodesCount === 1;
    var outerAxial = outerNodesCount % 2 === 0 || outerNodesCount === 1;
    if (innerAxial === outerAxial) outerPhaseShift = 0.5 / outerNodesCount;

    //override for core areas
    if (area === 0) outerPhaseShift = 0;

    //set focused node at center
    if (node === focusedNode) {
      node.position = { x: CenterPoint.x, y: CenterPoint.y };
    } else if (firstRelations.includes(node)) {
      var offset = {
        x:
          Math.cos((orderInCluster * (Math.PI * 2)) / innerNodesCount) *
          outerRadius *
          0.5,
        y:
          Math.sin((orderInCluster * (Math.PI * 2)) / innerNodesCount) *
          outerRadius *
          0.5,
      };
      node.position = {
        x: CenterPoint.x + offset.x,
        y: CenterPoint.y + offset.y,
      };
    }
    //outer areas
    else {
      offset = {
        x:
          -Math.sin(
            ((orderInCluster + outerPhaseShift) * (Math.PI * 2)) /
              outerNodesCount
          ) * outerRadius,
        y:
          -Math.cos(
            ((orderInCluster + outerPhaseShift) * (Math.PI * 2)) /
              outerNodesCount
          ) * outerRadius,
      };
      node.position = {
        x: CenterPoint.x + offset.x,
        y: CenterPoint.y + offset.y,
      };
    }
  });
  // console.log("area " + area)
  // console.dir(alphaSortedChildren)
  // console.log("radial layout done")
  return RadialGraph;
};

const GetKernelsOfArea = (
  NodeList: ICourseNode[],
  area: number
): ICourseNode[] => {
  let KernelsOfArea;

  if (area >= 1 && area <= 6) {
    KernelsOfArea = NodeList.filter(
      (node) => node.data.courseIsKernel && node.homeArea === area
    );
    // console.log("Kernels of Area " + area)
    // console.dir(KernelsOfArea)
  }
  //areas 7,8,0
  else {
    // For area 0 (Core) exclude courses whose courseAreas is an empty array
    if (area === 0) {
      KernelsOfArea = NodeList.filter(
        (node) =>
          node.homeArea === area &&
          Array.isArray(node.data.courseAreas) &&
          node.data.courseAreas.length > 0
      );
    } else {
      KernelsOfArea = NodeList.filter((node) => node.homeArea === area);
    }
    // console.log("Kernels of Area " + area)
    // console.dir(KernelsOfArea)
  }
  return KernelsOfArea;
};

const UpdateCourseNodeVisibility = (
  CourseNodeList: ICourseNode[],
  ExpansionState: boolean[]
) => {
  CourseNodeList = CourseNodeList.map((node) => {
    if (!ExpansionState[node.homeArea] || node.stylemode === "heatmap-xcl")
      //bypass for heatmap
      node.hidden = true;
    else node.hidden = false;
    return node;
  });
  return CourseNodeList;
};

const UpdateCourseEdgeVisibility = (
  CourseEdgeList: IConnector[],
  CourseNodeList: ICourseNode[],
  ClusterFoci: ICourseNode[] | undefined
) => {
  CourseEdgeList = CourseEdgeList.map((edge) => {
    // console.dir(edge);
    let src_node = CourseNodeList.find(
      (n) => n.data.courseCode === edge.sourceNodeRef.data.courseCode
    );
    let dst_node = CourseNodeList.find(
      (n) => n.data.courseCode === edge.destNodeRef.data.courseCode
    );

    // var connected = edge.destNodeRef.data.courseOptPrereqs
    // connected = connected.concat(edge.destNodeRef.data.courseCoreqs)
    // connected = connected.concat(edge.destNodeRef.data.coursePrereqs)

    // connected = connected.concat(edge.sourceNodeRef.data.courseOptPrereqs)
    // connected = connected.concat(edge.sourceNodeRef.data.courseCoreqs)
    // connected = connected.concat(edge.sourceNodeRef.data.courseOptPrereqs)

    // console.log("Clusterfoci")
    // console.dir(ClusterFoci)

    ClusterFoci?.map((el) => {
      return el?.data.courseCode;
    });
    var relationFociOverlap = []; //ClusterFociStr?.filter(value => connected.includes(value))
    // console.log("relation foci overlap")
    // console.dir(relationFociOverlap)
    // if (src_node?.stylemode.includes("heatmap")) {
    //   edge.styleMode = EdgeMode.HEATMAP;
    // }
    if (
      src_node?.hidden ||
      dst_node?.hidden ||
      src_node?.stylemode.includes("heatmap")
    ) {
      edge.styleMode = EdgeMode.HIDDEN;
      // console.dir(src_hidden || dst_hidden)
    }
    //highlight if cluster direct
    else if (
      ClusterFoci !== undefined &&
      (ClusterFoci.includes(src_node!) || ClusterFoci.includes(dst_node!))
    )
      edge.styleMode = EdgeMode.HIGHLIGHT;
    //highlight if cluster related
    else if (ClusterFoci !== undefined && relationFociOverlap!.length > 0)
      edge.styleMode = EdgeMode.SEMI_HIGHLIGHT;
    else edge.styleMode = EdgeMode.NORMAL;
    return edge;
  });
  // console.dir("Course Edge List")
  // console.dir(CourseEdgeList)
  return CourseEdgeList;
};

export const ShiftCourseHomeAreas = (
  CourseNodeList: ICourseNode[],
  area: number
) => {
  CourseNodeList.forEach((el) => {
    if (el.data.courseAreas && el.data.courseAreas.includes(area)) {
      el.homeArea = area;
    }
  });

  return CourseNodeList;
};

export const GetShiftAffectedCourseHomeAreas = (
  CourseNodeList: ICourseNode[],
  area: number
) => {
  var areasRet: number[] = [];
  CourseNodeList.forEach((el) => {
    //if move is imminent
    if (
      el.data.courseAreas &&
      el.data.courseAreas.includes(area) &&
      el.homeArea !== area
    ) {
      if (!areasRet.includes(el.homeArea)) areasRet.push(el.homeArea); //prevent duplicates
    }
  });

  return areasRet;
};

export const ReinjectCoursesAfterClusterClose = (
  CourseNodeList: ICourseNode[],
  CourseClusterExpansion: boolean[]
) => {
  // reinjection after layout fails
  CourseNodeList.forEach((node) => {
    var areas = node.data.courseAreas;
    var openAreas = areas ? areas.filter((a) => CourseClusterExpansion[a]) : []; // other open areas this node could be put in
    if (!CourseClusterExpansion[node.homeArea] && openAreas.length > 0) {
      // if the home area is closed, but it could belong in another area, write it to that area
      console.log(`reinjected ${node.data.courseCode} into ${openAreas[0]}`);
      node.homeArea = openAreas[0];
    }
  });
  return CourseNodeList;
};

export const GridLayoutCourseHeatmap = (
  CourseNodeList: ICourseNode[],
  heatmap_focus: HeatmapArea
) => {
  // This function does not modify CourseNodeList, but rather deep copies and returns a completely new array.

  // let GRID_H = 60
  let GRID_W = 8;
  let GRID_R = 120 * 1.125;

  // let UpdatedCourseNodeList = [...CourseNodeList]
  // let UpdatedCourseNodeList = CourseNodeList
  var ShownNodes: ICourseNode[] = [];
  var HiddenNodes: ICourseNode[] = [];

  // console.log("heatmap data codes")
  // UpdatedCourseNodeList.forEach(el => console.log(el.data.courseCode + " : " + GetValueOfHeatmapRelevance(el, heatmap_focus)))
  // console.log('end heatmap data codes')
  // ShownNodes = CourseNodeList.filter(el => GetValueOfHeatmapRelevance(el, heatmap_focus) > 0)

  //add relations as well
  // for (var i = 0; i < ShownNodes.length; i++) {
  //   var rel = GetCourseFirstRelations(CourseNodeList, ShownNodes[i].data.courseCode);
  //   for (var j = 0; j < rel.length; j++) {
  //     if (!RelatedToShownNodes.includes(rel[j]) && !ShownNodes.includes(rel[j])) {
  //       RelatedToShownNodes.push(rel[j])
  //     }
  //   }
  // }
  // ShownNodes = ShownNodes.concat(RelatedToShownNodes)

  var HighRelevanceNodes = CourseNodeList.filter(
    (el) => GetValueOfHeatmapRelevance(el, heatmap_focus) === 3
  ).sort((a, b) => a.data.courseCode.localeCompare(b.data.courseCode));
  var MidRelevanceNodes = CourseNodeList.filter(
    (el) => GetValueOfHeatmapRelevance(el, heatmap_focus) === 2
  ).sort((a, b) => a.data.courseCode.localeCompare(b.data.courseCode));
  var LowRelevanceNodes = CourseNodeList.filter(
    (el) => GetValueOfHeatmapRelevance(el, heatmap_focus) === 1
  ).sort((a, b) => a.data.courseCode.localeCompare(b.data.courseCode));

  // ShownNodes.sort((a, b) => GetValueOfHeatmapRelevance(b, heatmap_focus) - GetValueOfHeatmapRelevance(a, heatmap_focus))
  ShownNodes = HighRelevanceNodes.concat(MidRelevanceNodes, LowRelevanceNodes);
  HiddenNodes = CourseNodeList.filter((el) => !ShownNodes.includes(el));
  // HiddenNodes = JSON.parse(JSON.stringify(UpdatedCourseNodeList.filter(el => !ShownNodes.includes(el))))

  // UpdatedCourseNodeList = ShownNodes
  // HiddenNodes = UpdatedCourseNodeList.filter(el=> GetValueOfHeatmapRelevance(el, heatmap_focus) <= 0)

  // pl("all", UpdatedCourseNodeList)

  GRID_W = Math.min(Math.ceil(Math.sqrt(ShownNodes.length)), 6);
  // UpdatedCourseNodeList.sort((a, b) => ShownNodes.includes(a) !== null ? 0 : 1 - (ShownNodes.includes(b)===null?0:1))

  //deep copy to self
  ShownNodes = JSON.parse(JSON.stringify(ShownNodes));
  HiddenNodes = JSON.parse(JSON.stringify(HiddenNodes));

  //alters in place
  for (var i = 0; i < ShownNodes.length; i++) {
    ShownNodes[i].position = {
      x: ((i % GRID_W) - GRID_W / 2 + 0.5) * GRID_R,
      y: (Math.floor(i / GRID_W) - GRID_W / 2) * GRID_R,
    };
    ShownNodes[i].hidden = false;
  }
  for (i = 0; i < HiddenNodes.length; i++) {
    HiddenNodes[i].hidden = true;
  }

  // pl('shown', ShownNodes)
  // pl('hidden', HiddenNodes)

  // pl('cn', coursen)

  //   if (HiddenNodes.includes(UpdatedCourseNodeList[i])) {
  //     UpdatedCourseNodeList[i].position = { x: -1000, y: -1000 }
  //   } else {
  //     UpdatedCourseNodeList[i].position = { x: (i % GRID_W - GRID_W / 2) * GRID_R, y: (Math.floor(i / GRID_W) - GRID_W / 2) * GRID_R }

  //   }
  // }

  // for(var j =0; j < HiddenNodes.length; j++){
  //   HiddenNodes[j].hidden = true;
  //   HiddenNodes[j].position =  {x:-1000, y:-1000}
  //   console.log(`node ${HiddenNodes[j].data.courseCode} hidden = ${HiddenNodes[j].hidden}`)
  // }
  // HiddenNodes.forEach(el => {
  //   // var xel = { ...el }
  //   el.hidden = true
  //   el.stylemode = "heatmap-xcl"
  //   el.position = { x: -999, y: -999 }
  //   return el;
  // })
  // pl("mixconcat", HiddenNodes.concat(shownNodes))
  // pl("shown", ShownNodes)
  // pl("hidden", HiddenNodes)
  // pl("mixconcat", HiddenNodes.concat(ShownNodes))
  // UpdateCourseEdgeVisibility()
  // UpdateCourseNodeVisibility()

  // return JSON.parse(JSON.stringify(HiddenNodes.concat(ShownNodes)))
  return HiddenNodes.concat(ShownNodes);
};

export const pl = (name, obj) => {
  // console.log("---")
  // console.log(name)
  // console.dir(obj)
  // console.log("---")
};

const GetValueOfHeatmapRelevance = (
  CourseNode: ICourseNode,
  heatmap_focus: HeatmapArea
) => {
  if (
    CourseNode.data.courseHeatmapAreas === null ||
    CourseNode.data.courseAreas?.length === 0
  ) {
    return -1;
  } else
    return CourseNode.data.courseHeatmapAreas![heatmap_focus.areaIndex]
      .relevance;
};

// const numConnectionsComparator = (a: ICourseNode, b: ICourseNode) => {
//   let numCa = a.data.courseOptPrereqs
//     .concat(a.data.coursePrereqs)
//     .concat(a.data.courseCoreqs)
//     .filter((el) => el !== "none").length;
//   let numCb = b.data.courseOptPrereqs
//     .concat(b.data.coursePrereqs)
//     .concat(b.data.courseCoreqs)
//     .filter((el) => el !== "none").length;
//   return numCb - numCa;
// };

export const extractCoreAngleArray = (
  NodeList: ICourseNode[],
  focusedNode: ICourseNode
) => {
  // var RadialGraph: ICourseNode[] = NodeList
  var alphaSortedChildren: ICourseNode[];
  var retAngleArray: number[] = [];
  // Exclude courses with empty courseAreas so they don't appear in Core cluster
  alphaSortedChildren = NodeList.filter(
    (el) =>
      el.homeArea === 0 &&
      Array.isArray(el.data.courseAreas) &&
      el.data.courseAreas.length > 0
  )
    .filter((el) => el !== focusedNode)
    .sort((a, b) => CoreCourseComparator(a.data.courseCode, b.data.courseCode))
    .reverse();

  var firstRelations;
  var outerRelations: ICourseNode[];

  if (focusedNode != null) {
    firstRelations = GetCourseFirstRelations(
      NodeList,
      focusedNode.data.courseCode
    ).filter((n) => n.homeArea === 0);
    outerRelations = alphaSortedChildren.filter(
      (n) => !firstRelations.includes(n)
    );
  } else {
    firstRelations = [];
    outerRelations = alphaSortedChildren;
  }

  // var innerAxial = firstRelations % 2 === 0 || innerNodesCount === 1;
  // var outerAxial = outerNodesCount % 2 === 0 || outerNodesCount === 1;
  // if (innerAxial && outerAxial) outerPhaseShift = 0.5
  // outerNodesCount -= innerNodesCount

  var currSem = "XX";
  // console.log('---' + outerPhaseShift + '---')
  for (var i = 0; i < outerRelations.length; i++) {
    var ng = ((i - 0.5) * (Math.PI * 2)) / outerRelations.length;
    var sem = CoreCourseToSemesterString(outerRelations[i].data.courseCode);
    if (sem !== currSem) {
      retAngleArray.push(ng);
      // console.log(outerRelations.length)
      // console.log(innerNodesCount)
      // console.log(outerPhaseShift)
      // console.log(`pushed angle ${ng} of course ${outerRelations[i].data.courseCode}`)
      currSem = sem;
    }
  }

  // console.log("area " + area)
  // console.dir(alphaSortedChildren)
  // console.log("radial layout done")
  return retAngleArray;
};

export {
  GetKernelsOfArea,
  RadialLayoutCourseNodes,
  UpdateCourseNodeVisibility,
  UpdateCourseEdgeVisibility,
};
